import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/http";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandItem, CommandEmpty } from "@/components/ui/command";
import { Merge, Smartphone, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/** "Send login link" — texts the client a one-time link that signs them into their account. */
export const PortalInviteButton = ({ client }) => {
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/portal-invite`, { channel: "sms" });
      toast.success(data.has_account ? "Login link texted" : "Account set-up link texted");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't send"); }
    finally { setBusy(false); }
  };
  if (!client.phone) return null;
  return (
    <button onClick={send} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary hover:text-primary transition-colors disabled:opacity-50" data-testid="file-portal-invite" title="Text the client a link to sign in / set up their account">
      <Smartphone className="h-3.5 w-3.5" />{busy ? "Sending…" : "Send login link"}
    </button>
  );
};

/** Merge another file into this one. Suggests likely duplicates (same mobile / email / name); any client can be searched. */
export const MergeClientDialog = ({ client, onMerged }) => {
  const [open, setOpen] = useState(false);
  const [dups, setDups] = useState([]);
  const [pick, setPick] = useState(null);
  const [clients, setClients] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setPick(null);
    api.get(`/clients/${client.id}/duplicates`).then(({ data }) => setDups(data)).catch(() => setDups([]));
    api.get("/clients").then(({ data }) => setClients((data || []).filter((c) => c.id !== client.id))).catch(() => {});
  }, [open, client.id]);
  useEffect(() => {
    api.get(`/clients/${client.id}/duplicates`).then(({ data }) => setDups(data)).catch(() => {});
  }, [client.id]);
  const merge = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/merge`, { source_id: pick.id });
      const n = Object.values(data.moved || {}).reduce((a, b) => a + b, 0);
      toast.success(`Merged ${pick.name} into ${client.name}${n ? ` · ${n} records moved` : ""}`);
      setOpen(false); onMerged?.(data.client);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't merge"); }
    finally { setBusy(false); }
  };
  const Row = ({ c, reasons }) => (
    <button type="button" onClick={() => setPick(c)} className={`w-full text-left rounded-lg border px-3 py-2 text-sm flex items-center justify-between gap-2 transition-colors ${pick?.id === c.id ? "border-primary bg-primary/5" : "hover:border-primary/50"}`} data-testid={`merge-pick-${c.id}`}>
      <div className="min-w-0"><div className="font-medium truncate">{c.name}</div><div className="text-xs text-muted-foreground truncate">{[c.phone, c.email].filter(Boolean).join(" · ")}{c.visits != null ? ` · ${c.visits} visit${c.visits === 1 ? "" : "s"}` : ""}</div></div>
      {reasons?.length > 0 && <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 rounded-full px-2 py-0.5 shrink-0">{reasons.join(", ")}</span>}
    </button>
  );
  return (
    <>
      <button onClick={() => setOpen(true)} className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition-colors ${dups.length ? "border-amber-300 bg-amber-50 text-amber-800 hover:border-amber-400" : "border-border hover:border-primary hover:text-primary"}`} data-testid="file-merge-btn">
        {dups.length ? <AlertTriangle className="h-3.5 w-3.5" /> : <Merge className="h-3.5 w-3.5" />}{dups.length ? `${dups.length} possible duplicate${dups.length > 1 ? "s" : ""}` : "Merge duplicate"}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="merge-dialog">
          <DialogHeader><DialogTitle className="font-heading">Merge into {client.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Pick the duplicate file. Its bookings, invoices, forms, charts, photos, credit and points move into <b>{client.name}</b>'s file and the duplicate is removed. This can't be undone.</p>
          {dups.length > 0 && <div className="space-y-1.5" data-testid="merge-suggestions"><div className="text-xs font-medium text-muted-foreground">Looks like the same person</div>{dups.map((c) => <Row key={c.id} c={c} reasons={c.reasons} />)}</div>}
          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <button type="button" className="h-10 w-full rounded-lg border border-dashed border-input px-3 text-sm text-muted-foreground flex items-center gap-2" data-testid="merge-search"><Search className="h-4 w-4" />Search another client…</button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
              <Command>
                <CommandInput placeholder="Name or mobile…" data-testid="merge-search-input" />
                <CommandList><CommandEmpty>No client found.</CommandEmpty>
                  {clients.slice(0, 400).map((c) => <CommandItem key={c.id} value={`${c.name} ${c.phone || ""}`} onSelect={() => { setPick(c); setSearchOpen(false); }} data-testid={`merge-search-opt-${c.id}`}>{c.name}<span className="ml-auto text-xs text-muted-foreground">{c.phone}</span></CommandItem>)}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {pick && !dups.some((d) => d.id === pick.id) && <Row c={pick} />}
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-lg" disabled={!pick || busy} onClick={merge} data-testid="merge-confirm">{busy ? "Merging…" : pick ? `Merge ${pick.name.split(" ")[0]} into this file` : "Merge"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
